import React, { useState, useEffect, useRef } from "react";
import { useUserProfile } from "../hooks/useUserProfile";
import { LuChevronDown, LuLogOut, LuUser } from "react-icons/lu";
import { FaBullseye, FaSearch } from "react-icons/fa";
import RoleSwitcher from "./RoleSwitcher";
import { useMapaBuscador } from "../context/MapaBuscadorContext";

/**
 * Header del usuario Comercial (typeuser 4). Basado en HeaderEmployee pero sin
 * badge de edificio (el comercial no está atado a un edificio). En mobile
 * muestra la marca a la izquierda; el menú de usuario (perfil / logout) a la derecha.
 */
const HeaderComercial = ({ setSelected, onLogout = () => {}, mobile = false }) => {
  const { photoURL, initial } = useUserProfile();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  // Buscador de direcciones: solo visible cuando el mapa está montado (registró
  // su handler en el context). Al enviarlo, el mapa centra/pinea o abre alta manual.
  const { activo: buscadorActivo, buscando, buscar } = useMapaBuscador();
  const [texto, setTexto] = useState("");
  const searchInputRef = useRef(null);
  const buscarRef = useRef(buscar);
  useEffect(() => { buscarRef.current = buscar; }, [buscar]);
  const submitBusqueda = (e) => { e.preventDefault(); buscar(texto); };

  // Autocompletado de Google Places sobre el input (el script ya lo carga el mapa).
  // Al elegir una sugerencia con geometría, va directo al mapa sin re-geocodear.
  useEffect(() => {
    if (!buscadorActivo || !searchInputRef.current) return;
    if (!window.google || !window.google.maps || !window.google.maps.places) return;
    const ac = new window.google.maps.places.Autocomplete(searchInputRef.current, {
      componentRestrictions: { country: "uy" },
      fields: ["geometry", "formatted_address"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place && place.geometry && place.geometry.location) {
        buscarRef.current({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          direccion: (place.formatted_address || "").replace(/, Uruguay$/, ""),
        });
      }
    });
    return () => { if (window.google) window.google.maps.event.removeListener(listener); };
  }, [buscadorActivo]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === "Escape") setShowMenu(false); };
    if (showMenu) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showMenu]);

  return (
    <header style={styles.header} role="banner">
      <div style={styles.left}>
        {mobile && (
          <div
            style={styles.brand}
            onClick={() => setSelected("homecomercial")}
            role="button"
            tabIndex={0}
            aria-label="Ir al inicio"
            onKeyDown={(e) => e.key === "Enter" && setSelected("homecomercial")}
          >
            {/* Solo el ícono (botón "inicio"): sin el texto, el buscador gana ancho. */}
            <FaBullseye size={18} color="#0080ff" />
          </div>
        )}
      </div>

      <div style={styles.center}>
        {buscadorActivo && (
          <form onSubmit={submitBusqueda} style={styles.searchForm}>
            <FaSearch size={13} color="#9ca3af" style={styles.searchIcon} />
            <input
              ref={searchInputRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={mobile ? "Buscar dirección…" : "Buscar dirección o padrón en el mapa…"}
              aria-label="Buscar dirección o padrón en el mapa"
              style={styles.searchInput}
            />
            <button type="submit" disabled={buscando} style={styles.searchBtn} aria-label="Buscar">
              {buscando ? "…" : "Ir"}
            </button>
          </form>
        )}
      </div>

      <div style={styles.right} ref={menuRef}>
        <div
          style={styles.userMenu}
          onClick={() => setShowMenu(!showMenu)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setShowMenu(!showMenu)}
          aria-expanded={showMenu}
          aria-haspopup="true"
        >
          {photoURL ? (
            <img src={photoURL} alt="Tu perfil" style={styles.avatar} />
          ) : (
            <div style={styles.avatarLetter} aria-hidden="true">{initial}</div>
          )}
          <LuChevronDown size={10} color="#6b7280" />
          {showMenu && (
            <div style={styles.dropdown} role="menu">
              <div
                style={styles.dropdownItem}
                onClick={() => { setSelected("perfil"); setShowMenu(false); }}
                role="menuitem" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") { setSelected("perfil"); setShowMenu(false); } }}
              >
                <LuUser size={12} style={{ marginRight: 6 }} /> Mi Perfil
              </div>
              <RoleSwitcher variant="menu" />
              <div style={styles.dropdownDivider}></div>
              <div
                style={styles.dropdownItem}
                onClick={() => { onLogout?.(); setShowMenu(false); }}
                role="menuitem" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") { onLogout?.(); setShowMenu(false); } }}
              >
                <LuLogOut size={12} style={{ marginRight: 6 }} /> Cerrar Sesión
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const styles = {
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between", height: 56,
    padding: "0 16px", backgroundColor: "#ffffff", borderBottom: "1px solid #e5e7eb",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    flexShrink: 0, zIndex: 50,
  },
  left: { display: "flex", alignItems: "center", gap: 12 },
  brand: { display: "flex", alignItems: "center", cursor: "pointer" },
  center: { display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: "0 8px", minWidth: 0 },
  searchForm: { display: "flex", alignItems: "center", gap: 6, width: "100%", maxWidth: 460, position: "relative" },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" },
  searchInput: {
    flex: 1, minWidth: 0, height: 38, padding: "0 8px 0 34px", boxSizing: "border-box",
    border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14,
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif", background: "#f9fafb", color: "#111827", outline: "none",
  },
  searchBtn: {
    height: 38, padding: "0 14px", border: "none", borderRadius: 8, background: "#0080ff", color: "#fff",
    fontSize: 14, fontFamily: "inherit", cursor: "pointer", flexShrink: 0,
  },
  right: { display: "flex", alignItems: "center", gap: 8 },
  userMenu: { display: "flex", alignItems: "center", gap: 6, cursor: "pointer", position: "relative", padding: "4px 8px", borderRadius: 8 },
  avatar: { width: 32, height: 32, borderRadius: "50%", objectFit: "cover" },
  avatarLetter: { width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0080ff", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 400 },
  dropdown: { position: "absolute", top: "100%", right: 0, marginTop: 4, backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: 160, zIndex: 200, overflow: "hidden" },
  dropdownItem: { padding: "10px 16px", fontSize: 14, color: "#374151", cursor: "pointer", display: "flex", alignItems: "center", transition: "background 0.15s" },
  dropdownDivider: { height: 1, backgroundColor: "#e5e7eb" },
};

export default HeaderComercial;
